import { useEffect, useRef, useState, useMemo } from "react";
import { useInterval } from "react-use";
import i18n from "../../../../i18n";
import { baseTransKey } from "../takeAssessmentHelper";
import { VerityAssessment } from "@verity/api-client/types";
import { isBetweenTimes } from "../../../../helpers/time";

function getTimerParts(totalSeconds: number): { count: number; unit: string } {
	const days = Math.floor(totalSeconds / 86400);
	if (days >= 1) return { count: days, unit: i18n.t(`${baseTransKey}.timer.daysUnit`, { count: days }) as string };
	const hours = Math.floor(totalSeconds / 3600);
	if (hours >= 1) return { count: hours, unit: i18n.t(`${baseTransKey}.timer.hoursUnit`, { count: hours }) as string };
	const minutes = Math.floor(totalSeconds / 60);
	if (minutes >= 1)
		return { count: minutes, unit: i18n.t(`${baseTransKey}.timer.minutesUnit`, { count: minutes }) as string };
	return { count: totalSeconds, unit: i18n.t(`${baseTransKey}.timer.secondsUnit`, { count: totalSeconds }) as string };
}

export default function useQuizTimer(
	verityAssessment: VerityAssessment | undefined,
	sessionStartedAt: string | undefined,
	isInstructor: boolean, // no timer for instructor
	onExpire?: () => void,
) {
	const [now, setNow] = useState(Date.now());
	const onExpireRef = useRef(onExpire);
	onExpireRef.current = onExpire;

	const hasExpiredRef = useRef(false);

	useInterval(() => setNow(Date.now()), isInstructor ? null : 1000);

	const windowStatus = useMemo((): {
		isWithinWindow: boolean;
		isLateSubmission: boolean;
		timeBeforeStart: number | undefined;
		isAfterWindow: boolean;
		isBeforeWindow: boolean;
	} => {
		if (!verityAssessment || isInstructor)
			return {
				isWithinWindow: isInstructor,
				isLateSubmission: false,
				timeBeforeStart: undefined,
				isAfterWindow: false,
				isBeforeWindow: false,
			};

		const nowDate = new Date(now);
		const start = new Date(verityAssessment.startTime);
		const end = new Date(verityAssessment.endTime);

		const late =
			verityAssessment.allowLateSubmission && verityAssessment.lateSubmissionDate
				? new Date(verityAssessment.lateSubmissionDate)
				: null;

		const isBetweenStartAndEnd = isBetweenTimes(nowDate, start, end);
		const isBetweenEndAndLateSubmission = late ? isBetweenTimes(nowDate, end, late) : false;

		const nowTime = nowDate.getTime();

		const timeBeforeStart = Math.max(0, start.getTime() - nowTime);
		const isBeforeWindow = timeBeforeStart > 0;

		const isWithinWindow = isBetweenStartAndEnd || isBetweenEndAndLateSubmission;

		const isAfterWindow = !isWithinWindow && (late ? nowTime > late.getTime() : nowTime > end.getTime());
		return {
			isWithinWindow,
			isLateSubmission: isBetweenEndAndLateSubmission,
			timeBeforeStart,
			isAfterWindow,
			isBeforeWindow,
		};
	}, [verityAssessment, isInstructor, now]);

	const timerStatus = useMemo(() => {
		if (!verityAssessment?.duration || verityAssessment.duration <= 0 || !sessionStartedAt || isInstructor) {
			return { isExpired: false };
		}

		const durationMs = verityAssessment.duration * 60 * 1000;
		const endTime = new Date(sessionStartedAt).getTime() + durationMs;
		const remaining = Math.max(0, Math.floor((endTime - now) / 1000));

		return {
			remainingSeconds: remaining,
			isExpired: remaining <= 0,
			endTime,
		};
	}, [verityAssessment?.duration, sessionStartedAt, isInstructor, now]);

	useEffect(() => {
		if (isInstructor) return; // no timer for instructor

		const shouldExpire =
			timerStatus.isExpired && sessionStartedAt && !windowStatus.isLateSubmission && !hasExpiredRef.current;

		// If expired and not within the late submission window, trigger onExpire
		if (shouldExpire) {
			onExpireRef.current?.();
			hasExpiredRef.current = true;
		}
	}, [timerStatus.isExpired, sessionStartedAt, windowStatus.isLateSubmission, isInstructor]);

	useEffect(() => {
		// Reset expired status when a new session starts
		if (!timerStatus.isExpired) {
			hasExpiredRef.current = false;
		}
	}, [sessionStartedAt, timerStatus.isExpired]);

	return {
		timerStatus,
		windowStatus,
		timerParts:
			typeof timerStatus.remainingSeconds === "number"
				? {
						prefix: i18n.t(`${baseTransKey}.timer.endsIn_prefix`) as string,
						...getTimerParts(timerStatus.remainingSeconds),
					}
				: undefined,
	};
}

export type QuizTimerStatus = ReturnType<typeof useQuizTimer>;
